/* Shared, DOM-free contracts for explicit quick-add and cart variant replacement. */
(() => {
  const positive = (value) =>
    Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const variantId = (item) => Number(item.variant_id || item.id);
  const fault = (code) => Object.assign(new Error(code), { code });
  const stable = (value) =>
    JSON.stringify(
      Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)),
    );
  const identity = (item) =>
    `${variantId(item)}:${stable(item.properties)}:${item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ""}`;
  const inventory = (cart) =>
    cart.items
      .map((item) => `${identity(item)}:${item.quantity}`)
      .sort()
      .join("|");
  const validCart = (cart) =>
    cart &&
    Array.isArray(cart.items) &&
    cart.items.every(
      (item) =>
        positive(variantId(item)) &&
        positive(item.quantity) &&
        typeof item.key === "string" &&
        item.key.length > 0,
    ) &&
    new Set(cart.items.map((item) => item.key)).size === cart.items.length;
  const normalizedSize = (value) =>
    String(value)
      .trim()
      .toLowerCase()
      .replace(/^standardno$/, "standard");

  // Liquid serializes line properties as pairs; Ajax returns a record.
  // Normalize once at the editor boundary, before count and stale-line checks.
  function normalizeProperties(properties) {
    if (properties == null) return {};
    if (typeof properties !== "object") throw fault("incompatible");
    const entries = Array.isArray(properties)
      ? properties
      : Object.entries(properties);
    const keys = new Set();
    for (const entry of entries) {
      if (
        !Array.isArray(entry) ||
        entry.length !== 2 ||
        typeof entry[0] !== "string" ||
        !entry[0] ||
        keys.has(entry[0]) ||
        (entry[1] !== null &&
          !["string", "number", "boolean"].includes(typeof entry[1]))
      )
        throw fault("incompatible");
      keys.add(entry[0]);
    }
    const result = Object.fromEntries(entries);
    // Match the existing meal JSON parser, not arbitrary user property text.
    if (typeof result["_Obroci-IDs"] === "string")
      result["_Obroci-IDs"] = result["_Obroci-IDs"].replace(
        /&quot;|&#34;/g,
        '"',
      );
    return result;
  }

  function resolveVariant(variants, options) {
    if (!Array.isArray(variants) || !Array.isArray(options)) return undefined;
    return variants.find(
      (variant) =>
        variant &&
        positive(variant.id) &&
        variant.available === true &&
        Array.isArray(variant.options) &&
        variant.options.length === options.length &&
        options.every(
          (value, index) => value && value === variant.options[index],
        ),
    );
  }

  function compatibleOptions(original, next, editableIndexes) {
    return (
      Array.isArray(original) &&
      Array.isArray(next) &&
      Array.isArray(editableIndexes) &&
      original.length === next.length &&
      original.every(
        (value, index) =>
          editableIndexes.includes(index) || value === next[index],
      )
    );
  }

  function remapMeals(properties, size, products) {
    if (!properties || !Array.isArray(products)) throw fault("incompatible");
    let selections;
    try {
      selections = JSON.parse(
        properties["_Obroci-IDs"].replace(/&quot;|&#34;/g, '"'),
      );
    } catch {
      throw fault("incompatible");
    }
    if (
      !Array.isArray(selections) ||
      !selections.length ||
      !positive(properties["_Obroci-count"])
    )
      throw fault("incompatible");
    let count = 0;
    const seen = new Set();
    const mapped = selections.map((entry) => {
      if (
        !positive(entry.productId) ||
        !positive(entry.variantId) ||
        !positive(entry.qty) ||
        seen.has(Number(entry.variantId))
      )
        throw fault("incompatible");
      seen.add(Number(entry.variantId));
      count += Number(entry.qty);
      const product = products.find(
        (candidate) =>
          candidate && Number(candidate.id) === Number(entry.productId),
      );
      const original = product?.variants?.find(
        (variant) => variant && Number(variant.id) === Number(entry.variantId),
      );
      if (!original || !Array.isArray(original.options))
        throw fault("incompatible");
      const sizeIndex = original.options.findIndex((value) =>
        ["standard", "xl"].includes(normalizedSize(value)),
      );
      const target =
        sizeIndex < 0
          ? original
          : product.variants.find(
              (variant) =>
                Array.isArray(variant?.options) &&
                normalizedSize(variant.options[sizeIndex]) ===
                  normalizedSize(size) &&
                compatibleOptions(original.options, variant.options, [
                  sizeIndex,
                ]),
            );
      if (!target || target.available !== true || !positive(target.id))
        throw fault("incompatible");
      return {
        productId: Number(entry.productId),
        variantId: Number(target.id),
        qty: Number(entry.qty),
      };
    });
    if (
      count !== Number(properties["_Obroci-count"]) ||
      new Set(mapped.map((entry) => entry.variantId)).size !== mapped.length
    )
      throw fault("incompatible");
    return { ...properties, "_Obroci-IDs": JSON.stringify(mapped) };
  }

  async function read(api) {
    const cart = await api.read();
    if (!validCart(cart)) throw fault("uncertain");
    return cart;
  }

  // Ajax has no atomic replace-variant endpoint. Add, verify, then remove the exact old key.
  // Unknown writes never retry. Rollback is allowed only after a confirmed rejection and exact read-back.
  async function replaceLine(api, expected, variant, properties) {
    if (!positive(variant.id) || variant.available !== true)
      throw fault("incompatible");
    const before = await read(api);
    const source = before.items.find((item) => item.key === expected.key);
    if (
      !source ||
      identity(source) !== identity(expected) ||
      source.quantity !== expected.quantity
    )
      throw fault("stale");
    if (variantId(source) === Number(variant.id)) return before;
    const item = {
      id: Number(variant.id),
      quantity: source.quantity,
      properties: { ...properties },
    };
    if (source.selling_plan_allocation)
      item.selling_plan = source.selling_plan_allocation.selling_plan.id;
    const targetIdentity = identity(item);
    const previousTargets = before.items.filter(
      (entry) => identity(entry) === targetIdentity,
    );
    if (previousTargets.length > 1) throw fault("incompatible");
    const previousQuantity = previousTargets[0]?.quantity || 0;
    const expectedAfterAdd = {
      items: before.items
        .filter((entry) => identity(entry) !== targetIdentity)
        .concat({
          ...item,
          variant_id: item.id,
          quantity: previousQuantity + source.quantity,
        }),
    };
    const expectedAfterReplace = {
      items: expectedAfterAdd.items.filter((entry) => entry.key !== source.key),
    };
    try {
      const added = await api.add(item);
      if (
        !Array.isArray(added?.items) ||
        !added.items.some(
          (entry) => variantId(entry) === item.id && positive(entry.quantity),
        )
      )
        throw fault("uncertain");
    } catch (error) {
      if (error.code === "rejected") throw error;
      throw fault("uncertain");
    }
    let afterAdd;
    try {
      afterAdd = await read(api);
      if (
        inventory(afterAdd) !== inventory(expectedAfterAdd) ||
        !afterAdd.items.some((entry) => entry.key === source.key)
      )
        throw fault("uncertain");
    } catch {
      throw fault("uncertain");
    }
    try {
      const removed = await api.change(source.key, 0);
      if (
        !validCart(removed) ||
        inventory(removed) !== inventory(expectedAfterReplace)
      )
        throw fault("uncertain");
    } catch (error) {
      if (error.code !== "rejected") throw fault("uncertain");
      try {
        const current = await read(api);
        if (inventory(current) !== inventory(afterAdd))
          throw fault("uncertain");
        const target = current.items.find(
          (entry) => identity(entry) === targetIdentity,
        );
        const rolledBack = await api.change(target.key, previousQuantity);
        if (
          !validCart(rolledBack) ||
          inventory(rolledBack) !== inventory(before)
        )
          throw fault("uncertain");
      } catch {
        throw fault("uncertain");
      }
      throw fault("rejected");
    }
    try {
      const after = await read(api);
      if (inventory(after) !== inventory(expectedAfterReplace))
        throw fault("uncertain");
      return after;
    } catch {
      throw fault("uncertain");
    }
  }

  window.PrepMeQuickShopping = Object.freeze({
    normalizeProperties,
    resolveVariant,
    compatibleOptions,
    remapMeals,
    replaceLine,
  });
})();
